import { useMemo, useState } from "react";

import { MOEDAS_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useTableSettings, useToast } from "@/ds";
import type { HubTableDataTableProps } from "@/ds";
import { alterarSituacaoCotacaoReferencia } from "@/modules/financeiro-configuracoes";
import type { CotacaoReferenciaItem } from "@/modules/financeiro-configuracoes";
import { useMoedasCatalogo, useTableSettingsAppliedToast } from "@/shared/hooks";
import { hojeBrasilia } from "@/modules/obras";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { useAtivarDireto } from "../hooks/use-ativar-direto";
import { useMoedasLista } from "./hooks/use-moedas-lista";
import {
  MOEDAS_ALL_COLUMNS,
  MOEDAS_EXPORT_INDISPONIVEL,
  MOEDAS_FILTRO_TODAS,
  MOEDAS_TABLE_LABEL,
  MOEDAS_TABLE_MIN_WIDTH,
  MOEDA_TODAS,
  SITUACAO_OPCOES,
} from "./moedas.constants";
import type { MoedasColumnKey } from "./moedas.constants";
import {
  formatCadastradaEm,
  formatCompetencia,
  formatCotacao,
  tagsDaCotacao,
} from "./moedas.utils";
import {
  ATIVADA_COM_SUCESSO,
  ATIVAR_ERRO,
} from "./situacao-cotacao-dialog/situacao-cotacao-dialog.constants";
import type { AcaoPendente, MoedaLinha, MoedasViewModel } from "./types-moedas";

const DATA_TABLE_PROPS: HubTableDataTableProps = {
  scrollable: true,
  tableStyle: { minWidth: MOEDAS_TABLE_MIN_WIDTH },
  "aria-label": MOEDAS_TABLE_LABEL,
};

export function useMoedas(): MoedasViewModel {
  const { hasPermissao } = usePermissoes();
  const podeEditar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_CONFIGURACAO);
  const [acaoPendente, setAcaoPendente] = useState<AcaoPendente | null>(null);

  const settings = useTableSettings<MoedasColumnKey>({
    densityKey: MOEDAS_TABLE_DENSITY_KEY,
    allColumns: MOEDAS_ALL_COLUMNS,
  });
  const notifySettingsApplied = useTableSettingsAppliedToast(useToast());
  const lista = useMoedasLista();
  const { moedas } = useMoedasCatalogo();
  const catalogo = useMemo(() => moedas.filter((moeda) => !moeda.nacional), [moedas]);

  const ativarDireto = useAtivarDireto<CotacaoReferenciaItem>({
    enviar: (cotacao, idempotencyKey) =>
      alterarSituacaoCotacaoReferencia(cotacao.id, "ATIVA", undefined, { idempotencyKey }),
    sucesso: () => ATIVADA_COM_SUCESSO,
    aoConcluir: lista.reload,
    erroPadrao: ATIVAR_ERRO,
  });
  const abrirAcao = (acao: AcaoPendente) => {
    if (acao.tipo === "ativar") void ativarDireto(acao.cotacao);
    else setAcaoPendente(acao);
  };

  const mesAtual = hojeBrasilia().slice(0, 7);
  const linhas = lista.items.map((cotacao) =>
    montarLinha(cotacao, mesAtual, podeEditar, abrirAcao),
  );

  const hasFilters = lista.moedaId !== MOEDA_TODAS || lista.situacao !== "TODAS";
  const { semCotacoes } = lista;
  const novaCotacao = podeEditar ? () => setAcaoPendente({ tipo: "nova-cotacao" }) : undefined;

  const fecharAcao = () => setAcaoPendente(null);
  const dialogo: MoedasViewModel["dialogo"] =
    acaoPendente?.tipo === "nova-cotacao"
      ? {
          moedas: catalogo,
          onClose: fecharAcao,
          onConcluido: () => {
            fecharAcao();
            lista.reload();
          },
        }
      : null;
  const situacaoDialogo: MoedasViewModel["situacaoDialogo"] =
    acaoPendente?.tipo === "inativar"
      ? {
          cotacao: acaoPendente.cotacao,
          onClose: fecharAcao,
          onConcluido: () => {
            fecharAcao();
            lista.reload();
          },
        }
      : null;

  return {
    filtroMoeda: {
      value: lista.moedaId,
      options: [
        { label: MOEDAS_FILTRO_TODAS, value: MOEDA_TODAS },
        ...catalogo.map((moeda) => ({ label: `${moeda.codigo} · ${moeda.nome}`, value: moeda.id })),
      ],
      onChange: lista.setMoedaId,
    },
    menu: {
      abrirConfiguracoes: settings.dialog.open,
      exportar: () => undefined,
      exportDesabilitado: true,
      exportMotivo: MOEDAS_EXPORT_INDISPONIVEL,
    },
    configuracoes: { dialog: settings.dialog, onApplied: notifySettingsApplied },
    mostrarFiltroMoeda: !semCotacoes,
    filtroSituacao: {
      value: lista.situacao,
      options: [...SITUACAO_OPCOES],
      onChange: lista.setSituacao,
    },
    novaCotacao,
    tabela: {
      items: linhas,
      total: lista.total,
      status: lista.verificandoVazio ? "loading" : lista.status,
      errorMessage: lista.errorMessage,
      isFirstLoad: lista.status === "loading" && linhas.length === 0,
      reload: lista.reload,
      table: lista.tabela,
      settings,
      dataTableProps: DATA_TABLE_PROPS,
      mostrarAcoes: podeEditar,
      semCotacoes,
      hasFilters,
      onClear: hasFilters ? lista.limparFiltros : undefined,
      novaCotacao,
    },
    dialogo,
    situacaoDialogo,
  };
}

function montarLinha(
  cotacao: CotacaoReferenciaItem,
  mesAtual: string,
  podeEditar: boolean,
  abrir: (acao: AcaoPendente) => void,
): MoedaLinha {
  const competencia = formatCompetencia(cotacao.competencia);
  const tipo = cotacao.situacao === "ATIVA" ? "inativar" : "ativar";
  const nome = `${tipo === "inativar" ? "Inativar" : "Ativar"} cotação ${cotacao.moeda.codigo} ${competencia}`;

  return {
    id: cotacao.id,
    cotacao,
    competencia,
    tags: tagsDaCotacao(cotacao, mesAtual),
    moeda: `${cotacao.moeda.codigo} · ${cotacao.moeda.nome}`,
    taxa: formatCotacao(cotacao.cotacao),
    cadastradaEm: formatCadastradaEm(cotacao.cadastradaEm),
    cadastradaPor: cotacao.cadastradaPor,
    inativa: cotacao.situacao === "INATIVA",
    acao: podeEditar
      ? {
          tipo,
          label: nome,
          testId: `moedas-acao-${cotacao.id}`,
          executar: () => abrir({ tipo, cotacao }),
        }
      : null,
  };
}
